import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import { get, post } from "../../services/api";
import { customTableStyles } from "../../assets/CustomTableStyles";
import Select from "react-select";
import { toast } from "react-toastify";
import DropdownWithCustomValuesModal from "../../components/DropdownWithCustomValuesModal";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";

function TimeSlots() {
  const token = localStorage.getItem("token");
  const [timeSlots, setTimeSlots] = useState([{ startTime: "", endTime: "" }]);
  const [selectedDates, setSelectedDates] = useState([new Date()]); // Default to today
  const [selectedDay, setSelectedDay] = useState(""); // Day selection
  const [assessmentSlotData, setAssessmentSlotData] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [slotTypes, setSlotTypes] = useState([]);
  const [customDropdownName, setCustomDropdownName] = useState("");
  const [
    isCustomDropdownValuesModalVisible,
    setIsCustomDropdownValuesModalVisible,
  ] = useState(false);
  const centerId = localStorage.getItem("centerId");

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    getValues,
    setValue,
    trigger,
    reset,
  } = useForm({
    defaultValues: {
      slotTypeId: "",
      day: "",
    },
  });

  // Fetch slot types
  const fetchCommonSettings = async (moduleName, setData) => {
    try {
      const response = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=${moduleName}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(response?.commonSettingDetail);
    } catch (error) {
      console.error(`Error fetching ${moduleName}:`, error);
    }
  };

  useEffect(() => {
    fetchCommonSettings("slotType", setSlotTypes);
  }, [token]);

  // Fetch assessment slot data
  useEffect(() => {
    fetchAssesmentSlotData();
  }, [token]);

  const fetchAssesmentSlotData = async () => {
    try {
      const response = await get(
        `CommonSettings/GetTimeSlotByAsync?CenterId=${centerId} `,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setAssessmentSlotData(response.timeSlot);
    } catch (error) {
      console.error("Error fetching centers:", error);
    }
  };

  // Handle edit
  const handleEdit = async (id) => {
    try {
      const response = await get(
        `CommonSettings/GetTimeSlotByIdAsync?id=${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const assessmentSlot = response?.timeSlot;

      if (assessmentSlot) {
        const slotDate = new Date(assessmentSlot.date);
        setSelectedDates([slotDate]);
        setSelectedDay(assessmentSlot.dayName || ""); // Set day name from API
        const newTimeSlots = [
          {
            startTime: assessmentSlot.fromTime,
            endTime: assessmentSlot.toTime,
          },
        ];
        setTimeSlots(newTimeSlots);
        setCurrentId(assessmentSlot.id);
        reset({
          slotTypeId: assessmentSlot.slotTypeId.toString(),
          day: assessmentSlot.dayName || "",
          startTime: assessmentSlot.fromTime,
          endTime: assessmentSlot.toTime,
        });
      }
    } catch (error) {
      console.error("Error editing slot:", error);
      toast.error("Error loading slot data");
    }
  };

  // Remove timeslot row
  const removeTimeSlot = (index) => {
    setTimeSlots((prev) => prev.filter((_, i) => i !== index));
  };

  // Convert time to 24-hour format
  const convertTo24Hour = (time) => {
    if (!time) return "";
    const [timePart, modifier] = time.split(" ");
    let [hours, minutes] = timePart.split(":");
    hours = parseInt(hours, 10);
    if (modifier === "PM" && hours !== 12) hours += 12;
    if (modifier === "AM" && hours === 12) hours = 0;
    return `${hours.toString().padStart(2, "0")}:${minutes}`;
  };

  // Handle save with validations
  const handleSave = async () => {
    const isValid = await trigger(["slotTypeId", "day"]);
    // if (!isValid) {
    //   toast.error("Please fill all required fields!");
    //   return;
    // }

    const filteredSlots = timeSlots.filter(
      (slot, index) => index === 0 || slot.startTime || slot.endTime
    );
    const slotTypeId = getValues("slotTypeId");
    const day = getValues("day");

    if (selectedDates.length < 1) {
      toast.error("This field is required.");
      return;
    }

    if (
      filteredSlots.length === 0 ||
      !filteredSlots.some((slot) => slot.startTime && slot.endTime)
    ) {
      toast.error("At least one valid time slot is required!");
      return;
    }

    const timeSlotsArray = [];
    selectedDates.forEach((dateString) => {
      const formattedDate = new Date(dateString).toISOString();

      filteredSlots.forEach((slot) => {
        if (slot.startTime && slot.endTime) {
          timeSlotsArray.push({
            id: currentId || 0,
            createdBy: localStorage.getItem("userId"),
            updatedBy: localStorage.getItem("userId"),
            date: formattedDate,
            dayName: day,
            fromTime: convertTo24Hour(slot.startTime),
            toTime: convertTo24Hour(slot.endTime),
            slotTypeId: Number(slotTypeId),
            centerId: centerId,
            slotTypeName:
              slotTypes.find((type) => type.id === Number(slotTypeId))?.value ||
              "",
          });
        }
      });
    });

    const payload = { timeSlots: timeSlotsArray };

    try {
      const response = await post(
        "CommonSettings/AddUpdateTimeSlotAsync",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.isSuccess) {
        toast.success("Slots saved successfully!");
        fetchAssesmentSlotData();
        handleReset();
      } else {
        toast.error("Error saving slots");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Error saving slots");
    }
  };

  // Handle reset
  const handleReset = () => {
    setSelectedDates([new Date()]);
    setSelectedDay("");
    setTimeSlots([{ startTime: "", endTime: "" }]);
    setCurrentId(null);
    reset({
      slotTypeId: "",
      day: "",
    });
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  };

  // DataTable columns
  const columns = [
    {
      accessorKey: "mrt-row-numbers",
      header: "Sr No.",
      enableColumnOrdering: false,
      enableEditing: false,
      enableSorting: false,
      size: 80,
      Cell: ({ row }) => row.index + 1,
    },
    {
      header: "Date",
      accessorKey: "date",
      Cell: ({ row }) => formatDate(row.original.date),
    },
    { header: "Day", accessorKey: "dayName" }, // Added Day column
    { header: "Slot Type", accessorKey: "slotTypeName" },
    { header: "Start Time", accessorKey: "fromTime" },
    { header: "End Time", accessorKey: "toTime" },
    {
      header: "Action",
      size: 120,
      Cell: ({ row }) => (
        <button
          type="button"
          className="btn p-0"
          onClick={() => handleEdit(row.original.id)}
        >
          <i className="fas fa-pencil"></i>
        </button>
      ),
    },
  ];

  const exportToExcel = () => {
    const filteredData = assessmentSlotData.map((row, index) => ({
      "Sr No": index + 1,
      Date: formatDate(row.date),
      Day: row.dayName,
      "Slot Type": row.slotTypeName,
      "Start Time": row.fromTime,
      "End Time": row.toTime,
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "TimeSlots");
    XLSX.writeFile(workbook, "TimeSlots.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;
    // doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Time Slots List";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const headers = [
      ["Sr No", "Date", "Day", "Slot Type", "Start Time", "End Time"],
    ];

    const bodyData = assessmentSlotData.map((row, index) => [
      index + 1,
      formatDate(row.date),
      row.dayName,
      row.slotTypeName,
      row.fromTime,
      row.toTime,
    ]);

    doc.autoTable({
      head: headers,
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("TimeSlotsList.pdf");
  };

  // Days array
  const days = [
    { name: "Monday" },
    { name: "Tuesday" },
    { name: "Wednesday" },
    { name: "Thursday" },
    { name: "Friday" },
    { name: "Saturday" },
    { name: "Sunday" },
  ];

  // TimeSlotRow component
  const TimeSlotRow = ({ slot, index }) => {
    return (
      <div className="row g-3 mb-3">
        <div className="col-md-5">
          <label className="form-label">Start Time</label>
          <input
            type="time"
            className="form-control"
            value={slot.startTime}
            onChange={(e) => {
              const newSlots = [...timeSlots];
              newSlots[index].startTime = e.target.value;
              setTimeSlots(newSlots);
            }}
          />
        </div>
        <div className="col-md-5">
          <label className="form-label">End Time</label>
          <input
            type="time"
            className="form-control"
            value={slot.endTime}
            onChange={(e) => {
              const newSlots = [...timeSlots];
              newSlots[index].endTime = e.target.value;
              setTimeSlots(newSlots);
            }}
          />
        </div>
        <div className="col-md-2 d-flex align-items-end">
          <div>
            <button
              type="button"
              className="btn py-1 px-2 btn-success me-1"
              onClick={() =>
                setTimeSlots((prev) => [
                  ...prev,
                  { startTime: "", endTime: "" },
                ])
              }
            >
              <i className="fa fa-plus"></i>
            </button>
            {index > 0 && (
              <button
                className="btn py-1 px-2 btn-danger"
                onClick={() => removeTimeSlot(index)}
              >
                <i className="fa fa-minus"></i>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Handle custom dropdown settings
  const handleCustomDropdownSetting = (dropdownName) => {
    setCustomDropdownName(dropdownName);
    setIsCustomDropdownValuesModalVisible(true);
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Time Slots</h2>
      </div>

      <div className="row g-3">
        <div className="col-md-5">
          <div className="card">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-5">
                  <label className="form-label">
                    Slot Type <span className="text-danger fs-9">*</span>
                  </label>
                  <div className="d-flex gap-2">
                    <div className="w-100">
                      <Controller
                        name="slotTypeId"
                        control={control}
                        rules={{ required: "This field is required." }} // Added validation
                        render={({ field }) => (
                          <select {...field} className="form-select">
                            <option value="">Select</option>
                            {slotTypes.map((item) => (
                              <option key={item.id} value={item.id}>
                                {item.value}
                              </option>
                            ))}
                          </select>
                        )}
                      />
                      {errors.slotTypeId && (
                        <small className="text-danger">
                          {errors.slotTypeId.message}
                        </small>
                      )}
                    </div>
                    <div>
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        style={{ padding: "5.5px 10px" }}
                        onClick={() => handleCustomDropdownSetting("slotType")}
                      >
                        <i className="fas fa-plus"></i>
                      </button>
                    </div>
                  </div>
                </div>
                {/* <div className="col-md-7">
                  <label className="form-label">
                    Date <span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={new Date().toISOString().split("T")[0]}
                    disabled
                  />
                </div> */}
                <div className="col-md-7">
                  <label className="form-label">
                    Day <span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="day"
                    control={control}
                    rules={{ required: "Day is required" }} // Added validation
                    render={({ field }) => (
                      <select
                        {...field}
                        className="form-select"
                        onChange={(e) => {
                          field.onChange(e);
                          setSelectedDay(e.target.value);
                        }}
                      >
                        <option value="">Select</option>
                        {days.map((day) => (
                          <option key={day.name} value={day.name}>
                            {day.name}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                  {errors.day && (
                    <small className="text-danger">{errors.day.message}</small>
                  )}
                </div>
              </div>
              <div className="time-slots-container mt-3">
                {timeSlots.map((slot, index) => (
                  <TimeSlotRow key={index} slot={slot} index={index} />
                ))}
                <div className="col-md-12 d-flex gap-2 mt-3">
                  {/* <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={handleReset}
                  >
                    Reset
                  </button> */}
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleSave}
                    disabled={isSubmitting}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-7">
          <div className="card">
            <div className="card-body">
              <CustomDatatableGrid
                columns={columns}
                data={assessmentSlotData}
                onExportExcel={exportToExcel}
                onExportPDF={exportToPDF}
              />
            </div>
            {isCustomDropdownValuesModalVisible && (
              <DropdownWithCustomValuesModal
                dropdownName={customDropdownName}
                onClose={() => {
                  setIsCustomDropdownValuesModalVisible(false);
                  if (customDropdownName === "slotType") {
                    fetchCommonSettings("slotType", setSlotTypes);
                  }
                }}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default TimeSlots;
